import { ApolloClient, ApolloError, useMutation } from "@apollo/client";
import { AccountErrorCode, useUserDetailsQuery } from "@dashboard/graphql";
import useLocalStorage from "@dashboard/hooks/useLocalStorage";
import useNavigator from "@dashboard/hooks/useNavigator";
import { useNotifier } from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { RootState } from "@dashboard/store";
import {
  checkIfCredentialsExist,
  isSupported as isCredentialsManagementAPISupported,
  login as loginWithCredentialsManagementAPI,
  saveCredentials,
} from "@dashboard/utils/credentialsManagement";
import isEmpty from "lodash/isEmpty";
import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

import { parseAuthError } from "../errors";
import { tokenCreate as tokenCreateMutation } from "../mutations";
import { authService } from "../services/authService";
import { resetSessionExpired, subscribeToSessionExpired } from "../sessionExpiry";
import {
  logout as logoutAction,
  setAuthenticated,
  setAuthenticating,
  setAuthErrors,
  setUser,
} from "../store/authSlice";
import {
  ExternalLoginInput,
  RequestExternalLoginInput,
  UserContext,
  UserContextError,
} from "../types";
import { useLastLoginMethod } from "./useLastLoginMethod";

interface UseAuthProviderOpts {
  apolloClient: ApolloClient<any>;
}

export function useAuthProvider({ apolloClient }: UseAuthProviderOpts): UserContext {
  const dispatch = useDispatch();
  const {
    authenticated,
    authenticating,
    user: authUser,
    errors: authErrors,
  } = useSelector((state: RootState) => state.auth);

  const [tokenCreate] = useMutation(tokenCreateMutation, { client: apolloClient });

  const navigate = useNavigator();
  const [requestedExternalPluginId] = useLocalStorage("requestedExternalPluginId", null);
  const [isCredentialsLogin, setIsCredentialsLogin] = useState(false);
  const [errors, setErrors] = useState<UserContextError[]>(authErrors);
  const permitCredentialsAPI = useRef(true);
  const { setLastLoginMethod } = useLastLoginMethod();
  const notify = useNotifier();
  const intl = useIntl();

  const handleLoginError = (error: ApolloError) => {
    const parsedErrors = parseAuthError(error);
    const finalErrors: UserContextError[] = parsedErrors.length
      ? parsedErrors
      : ["unknownLoginError"];

    setErrors(finalErrors);
    dispatch(setAuthErrors(finalErrors));
  };

  const handleLogout = useCallback(async () => {
    authService.clearTokens();
    dispatch(logoutAction());

    const hasCredentials = await checkIfCredentialsExist();

    if (isCredentialsManagementAPISupported && !!hasCredentials) {
      navigator.credentials.preventSilentAccess();
    }

    apolloClient.clearStore();
    navigate("/");
  }, [apolloClient, dispatch, navigate]);

  const handleLogin = async (email: string, password: string) => {
    if (isCredentialsLogin) {
      return;
    }

    try {
      setIsCredentialsLogin(true);
      dispatch(setAuthenticating(true));

      const result = await tokenCreate({
        variables: { email, password },
      });

      const data = result.data?.tokenCreate;
      const errorList = data?.errors?.map(({ code }) => code);

      const userLoggedInButHasNoPermissions = data?.user && isEmpty(data?.user?.userPermissions);

      if (userLoggedInButHasNoPermissions) {
        const err: UserContextError[] = ["noPermissionsError"];

        setErrors(err);
        dispatch(setAuthErrors(err));
        await handleLogout();

        return;
      }

      if (data?.token && !errorList?.length) {
        resetSessionExpired();
        authService.setAccessToken(data.token);

        if (data.refreshToken) {
          authService.setRefreshToken(data.refreshToken);
        }

        if (data.csrfToken) {
          authService.setCsrfToken(data.csrfToken);
        }

        if (data.user) {
          authService.setUser(data.user);
        }

        dispatch(
          setAuthenticated({
            user: data.user,
            token: data.token,
            csrfToken: data.csrfToken,
          }),
        );

        saveCredentials(data.user!, password);
        setLastLoginMethod("password");

        return data;
      } else {
        const userContextErrorList: UserContextError[] = [];

        errorList?.forEach(error => {
          switch (error) {
            case AccountErrorCode.LOGIN_ATTEMPT_DELAYED:
              userContextErrorList.push("loginAttemptDelay");
              break;
            case AccountErrorCode.INVALID_CREDENTIALS:
              userContextErrorList.push("invalidCredentials");
              break;
            default:
              userContextErrorList.push("loginError");
              break;
          }
        });

        setErrors(userContextErrorList);
        dispatch(setAuthErrors(userContextErrorList));
      }
    } catch (error) {
      if (error instanceof ApolloError) {
        handleLoginError(error);
      } else {
        const err: UserContextError[] = ["unknownLoginError"];

        setErrors(err);
        dispatch(setAuthErrors(err));
      }
    } finally {
      setIsCredentialsLogin(false);
      dispatch(setAuthenticating(false));
    }
  };

  const userDetails = useUserDetailsQuery({
    client: apolloClient,
    skip: !authenticated,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (authenticating && errors.length) {
      setErrors([]);
    }
  }, [authenticating, errors.length]);

  useEffect(() => {
    if (authenticated) {
      permitCredentialsAPI.current = true;
    }
  }, [authenticated]);

  useEffect(() => {
    if (
      !authenticated &&
      !authenticating &&
      !requestedExternalPluginId &&
      permitCredentialsAPI.current
    ) {
      permitCredentialsAPI.current = false;
      loginWithCredentialsManagementAPI(handleLogin);
    }
  }, [authenticated, authenticating, handleLogin, requestedExternalPluginId]);

  useEffect(() => {
    return subscribeToSessionExpired(() => {
      void handleLogout().finally(() => {
        notify({
          status: "error",
          text: intl.formatMessage(commonMessages.sessionExpired),
        });
      });
    });
  }, [handleLogout, intl, notify]);

  const handleRequestExternalLogin = async (
    _pluginId: string,
    _input: RequestExternalLoginInput,
  ) => {
    return null;
  };

  const handleExternalLogin = async (_pluginId: string | null, _input: ExternalLoginInput) => {
    return null;
  };

  useEffect(() => {
    if (userDetails.data?.me) {
      if (JSON.stringify(userDetails.data.me) !== JSON.stringify(authUser)) {
        dispatch(setUser(userDetails.data.me));
        authService.setUser(userDetails.data.me);
      }
    }
  }, [userDetails.data, authUser, dispatch]);

  const isAuthenticatingUser = authenticated && !authUser && userDetails.loading;

  return {
    login: handleLogin,
    requestLoginByExternalPlugin: handleRequestExternalLogin,
    loginByExternalPlugin: handleExternalLogin,
    logout: handleLogout,
    authenticating: (authenticating || isAuthenticatingUser) && !errors.length,
    isCredentialsLogin,
    authenticated: authenticated && !!authUser?.isStaff && !errors.length,
    user: userDetails.data?.me || authUser,
    refetchUser: userDetails.refetch,
    errors,
  };
}
